import { TimePickerInput, TimePickerType } from '@optools/components/datetime/time-picker-input';
import { Label } from '@optools/components/ui/label';
import { Video } from 'lucide-react';
import React, { useMemo } from 'react';

export interface TimeCodePickerProps {
  setTimecode: (timecode: string) => void;
  timecode: string;
}
const fieldValueRegex = /^[0-9][0-9]$/;
const validTimecodeRegex = /^[0-9][0-9]:[0-5][0-9]:[0-5][0-9]$/;

export function TimeCodePicker({ setTimecode, timecode }: TimeCodePickerProps) {
  const hourRef = React.useRef<HTMLInputElement>(null);
  const minuteRef = React.useRef<HTMLInputElement>(null);
  const secondRef = React.useRef<HTMLInputElement>(null);

  const [hours, minutes, seconds] = useMemo(() => timecode.split(':'), [timecode]);

  const setValueHandler = (value: string, type: TimePickerType) => {
    if (!fieldValueRegex.test(value)) {
      return;
    }

    let timecodeString = '00:00:00';
    switch (type) {
      case 'hours':
        timecodeString = `${value}:${minutes}:${seconds}`;
        break;
      case 'minutes':
        timecodeString = `${hours}:${value}:${seconds}`;
        break;
      case 'seconds':
        timecodeString = `${hours}:${minutes}:${value}`;
        break;
    }

    if (!validTimecodeRegex.test(timecodeString)) {
      return;
    }

    setTimecode(timecodeString);
  };

  return (
    <div className="flex items-end gap-2">
      <div className="flex flex-col gap-1 text-center">
        <Label className="text-xs" htmlFor="hours">
          Hours
        </Label>
        <TimePickerInput
          ref={hourRef}
          id="hours"
          setValue={setValueHandler}
          time={hours}
          timeType="hours"
          onFocusRight={() => minuteRef.current?.focus()}
        />
      </div>
      <div className="flex flex-col gap-1 text-center">
        <Label className="text-xs" htmlFor="minutes">
          Minutes
        </Label>
        <TimePickerInput
          ref={minuteRef}
          id="minutes"
          setValue={setValueHandler}
          time={minutes}
          timeType="minutes"
          onFocusLeft={() => hourRef.current?.focus()}
          onFocusRight={() => secondRef.current?.focus()}
        />
      </div>
      <div className="flex flex-col gap-1 text-center">
        <Label className="text-xs" htmlFor="seconds">
          Seconds
        </Label>
        <TimePickerInput
          ref={secondRef}
          id="seconds"
          setValue={setValueHandler}
          time={seconds}
          timeType="seconds"
          onFocusLeft={() => minuteRef.current?.focus()}
        />
      </div>
      <div className="flex h-10 items-center">
        <Video className="ml-2 h-4 w-4" />
      </div>
    </div>
  );
}
